import { useMemo } from 'react';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { getRoutesByTransport } from '@lp/features/MultiSuggest/utils/route';
import { RouteProvider } from '@lp/core/contexts/RouteContext';
import { AircraftUtilizationProvider } from '@lp/features/RoutePlanner/contexts/AircraftUtilizationContext';
import { RouteTypeEnum } from '@/api/apis/route';
import { CaseResourceType, ServiceType } from '@/api/apis/case';
import { DataCacheType } from '@/queries/consts';
import { RouteList } from './RouteList';
import { ResourcesList } from './ResourcesList';
import { CurrentProviderTimeValue } from './CurrentProviderTimeValue';
import { LogisticsInfoWarnings } from './LogisticsInfoWarnings';
import { AccommodationsList } from './AccommodationsList';

type LogisticsInfoProps = {
  serviceType: ServiceType;
};

export const LogisticsInfo = ({ serviceType }: LogisticsInfoProps) => {
  const { legs, accommodationLegs } = useRouteDetails(RouteTypeEnum.Main);
  const { resources } = useLogisticsData();
  const { list } = useRouteLocations(DataCacheType.Snapshot);
  const routeResources = useRouteResources(DataCacheType.Snapshot);

  const routeList = useMemo(() => {
    const aircrafts = resources.filter((r) => r.resourceType === CaseResourceType.Aircraft);

    const sortedLegs = legs.toSorted(
      (a, b) => new Date(a.departureTime).getTime() - new Date(b.departureTime).getTime(),
    );

    return getRoutesByTransport({ legs: sortedLegs, aircrafts });
  }, [legs, resources]);

  const isSurgeonRequired = serviceType === ServiceType.NopWithTmdxSurgeon;

  return (
    <RouteProvider locations={list} resources={routeResources}>
      <AircraftUtilizationProvider>
        <div className="flex flex-col p-4 gap-4 bg-base-80">
          <CurrentProviderTimeValue title="CURRENT" legs={legs} />
          <RouteList list={routeList} />
          <AccommodationsList legs={accommodationLegs} />
          {!!routeList.length && <ResourcesList isSurgeonRequired={isSurgeonRequired} />}
          <LogisticsInfoWarnings />
        </div>
      </AircraftUtilizationProvider>
    </RouteProvider>
  );
};
